import { useEffect, useState } from "react";
import type { AiRankingPayload, AiTop10Change } from "@dashboard/shared";
import { api } from "@/lib/api";

/** Der Ingest-Worker liest die Chart dreimal am Tag; 15 Min reichen, damit das rote
 *  Badge am News-Tab spaetestens eine Viertelstunde nach dem Abruf erscheint. */
const POLL_MS = 15 * 60 * 1000;
/** Aeltere Aenderungen zaehlen nie fuers Badge - sonst saehe ein neuer Browser
 *  (leerer localStorage) den ganzen Verlauf als "ungesehen". */
const RECENT_MS = 7 * 24 * 60 * 60 * 1000;
const SEEN_KEY = "news:ai-top10-seen";

export function useAiRanking(): AiRankingPayload | null {
  const [data, setData] = useState<AiRankingPayload | null>(null);
  useEffect(() => {
    let cancelled = false;
    const load = () =>
      api.aiRanking().then((d) => { if (!cancelled) setData(d); }).catch(() => {});
    load();
    const id = setInterval(load, POLL_MS);
    window.addEventListener("focus", load);
    return () => {
      cancelled = true;
      clearInterval(id);
      window.removeEventListener("focus", load);
    };
  }, []);
  return data;
}

/** Zeitpunkt der zuletzt gesehenen Top-10-Aenderung (ISO) - nur Komfort je Browser. */
export function readSeen(): string | null {
  try {
    return localStorage.getItem(SEEN_KEY);
  } catch {
    return null;
  }
}

export function writeSeen(at: string): void {
  try {
    localStorage.setItem(SEEN_KEY, at);
  } catch {
    /* privater Modus o. ae. - das Badge verschwindet dann nur fuer diese Sitzung */
  }
}

export function unseenChanges(data: AiRankingPayload | null, seenAt: string | null): AiTop10Change[] {
  if (!data) return [];
  const cutoff = Date.now() - RECENT_MS;
  return data.changes.filter((c) => (!seenAt || c.at > seenAt) && Date.parse(c.at) >= cutoff);
}

/** Die Aenderung, die zur aktuellen Reihenfolge gefuehrt hat, solange sie hoechstens
 *  7 Tage alt ist - nur dann markiert die Liste NEU/▲/▼. */
export function recentChange(data: AiRankingPayload): AiTop10Change | null {
  const latest = data.changes[0];
  return latest && latest.at === data.since && Date.parse(latest.at) >= Date.now() - RECENT_MS
    ? latest
    : null;
}

const OPEN_KEY = "news:ai-open-cards";

function loadOpen(): Set<string> {
  try {
    const raw = localStorage.getItem(OPEN_KEY);
    const arr = raw ? (JSON.parse(raw) as unknown) : [];
    return new Set(Array.isArray(arr) ? arr.filter((x): x is string => typeof x === "string") : []);
  } catch {
    return new Set();
  }
}

/** Auf-/Zugeklappt je Modell-Kachel; standardmaessig zu (Operator 2026-09-29), der
 *  Browser merkt sich, was aufgeklappt wurde - wie `brokers:open-tiles`. */
export function useOpenCard(key: string): [boolean, () => void] {
  const [open, setOpen] = useState(() => loadOpen().has(key));
  const toggle = () => {
    const next = !open;
    setOpen(next);
    try {
      const all = loadOpen();
      if (next) all.add(key);
      else all.delete(key);
      localStorage.setItem(OPEN_KEY, JSON.stringify([...all]));
    } catch {
      /* privater Modus o. ae. - dann merkt sich der Browser die Auswahl eben nicht */
    }
  };
  return [open, toggle];
}
